const WIDEVINE_DRM = 'Widevine';
const PLAY_READY_DRM = 'PlayReady';
export const FAIR_PLAY_DRM = 'FairPlay';

const MPD_VIDEO_EXTENSION = 'mpd';
const M3U8_VIDEO_EXTENSION = 'm3u8';

const FAIR_PLAY_CONFIG = {
  drmType: FAIR_PLAY_DRM,
  videoExtension: M3U8_VIDEO_EXTENSION,
};
const WIDEVINE_CONFIG = {
  drmType: WIDEVINE_DRM,
  videoExtension: MPD_VIDEO_EXTENSION,
};
const PLAY_READY_CONFIG = {
  drmType: PLAY_READY_DRM,
  videoExtension: MPD_VIDEO_EXTENSION,
};

// Detects if platform is iOS.
// Checks the platform ID for known iPhone / iPod / iPad markers and
// a special edge case for iPad on iOS-13+ using user agent + touch-support.
const isIOSPlatform = () =>
  navigator.platform.substr(0, 2) === 'iP' ||
  (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

const isFirefox = (userAgent: string) => userAgent.indexOf('Firefox') > -1;
const isSamsung = (userAgent: string) => userAgent.indexOf('SamsungBrowser') > -1;
const isOpera = (userAgent: string) =>
  userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1;
const isInternetExplorer = (userAgent: string) => userAgent.indexOf('Trident') > -1;
const isEdge = (userAgent: string) => userAgent.indexOf('Edge') > -1;
export const isChrome = (userAgent: string) => userAgent.indexOf('Chrome') > -1;
const isSafari = (userAgent: string) => userAgent.indexOf('Safari') > -1;

const isWKWebViewWithinSafari = (userAgent: string, windowNavigator: Navigator) => {
  // WKWebView is used within newer Safari for iOS versions
  return (
    userAgent.indexOf('Safari') !== -1 &&
    userAgent.indexOf('Version') !== -1 &&
    !(windowNavigator as any).standalone
  );
};

const isUiWebView = (indexedDB: boolean, lte9: boolean): boolean => {
  return (!indexedDB && lte9) || !window.statusbar.visible;
};

const isWKWebViewOrCordova = (webkit: any, lte9: boolean): boolean => {
  return webkit?.messageHandlers || !lte9 || indexedDB;
};

const isChromeInIos = (userAgent: string): boolean => {
  return userAgent.indexOf('CriOS') > -1;
};

const getNonIosSpecificInfos = (userAgent: string) => {
  // See https://castlabs.com/resources/drm-comparison/ for DRM compatibility
  // The order matters here, see https://developer.mozilla.org/en-US/docs/Web/API/Window/navigator
  if (isFirefox(userAgent) || isSamsung(userAgent) || isOpera(userAgent)) {
    return WIDEVINE_CONFIG;
  }
  if (isInternetExplorer(userAgent)) {
    return PLAY_READY_CONFIG;
  }
  if (isEdge(userAgent) || isChrome(userAgent)) {
    return WIDEVINE_CONFIG;
  }
  if (isSafari(userAgent)) {
    return FAIR_PLAY_CONFIG;
  }
  return { drmType: WIDEVINE_DRM, videoExtension: MPD_VIDEO_EXTENSION };
};

export const getBrowserSpecificInfos = () => {
  const userAgent = window.navigator.userAgent;

  if (isIOSPlatform()) {
    return FAIR_PLAY_CONFIG;
  }

  return getNonIosSpecificInfos(userAgent);
};

// Taken from: https://stackoverflow.com/a/30495399
// Re-adapted to:
//  - please the typescript compiler and return true
//    only for a true standalone WKWebView or Cordova app WKWebView.
//  - handle fishy iPad devices (see: https://stackoverflow.com/a/9039885)
export const runsInIOSApp = () => {
  if (isIOSPlatform()) {
    const lte9 = /constructor/i.test(window.HTMLElement as unknown as string);
    const windowNavigator = window.navigator;
    const userAgent = windowNavigator.userAgent;
    const indexedDB = !!window.indexedDB;
    const webkit = (window as any).webkit;

    if (isChromeInIos(userAgent)) {
      return false;
    }
    if (isWKWebViewWithinSafari(userAgent, windowNavigator)) {
      return false;
    }
    if (isUiWebView(indexedDB, lte9)) {
      return false;
    }
    if (isWKWebViewOrCordova(webkit, lte9)) {
      return true;
    }
  }
  return false;
};

export const isAndroidBefore10 = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Match returns an array with full match and captured groups
  // ex : ["android 9.0.0", "9.0.0"]
  const match = userAgent.match(/android\s([0-9.]*)/i);

  if (!match) {
    return false;
  }
  const version = match[1]; // "9.0.0"
  return Number.parseInt(version, 10) < 10;
};

// This method detects if device is a mobile thanks to the user agent
// See https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser/11381730#11381730
export const isMobile = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  return (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      userAgent,
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
      userAgent.substr(0, 4),
    )
  );
};

export const isBrowserCompatible = () => {
  const userAgent = window.navigator.userAgent;

  return runsInIOSApp || isChrome(userAgent) || isSafari(userAgent);
};

export const windowPlacementFeatureEnabled = () => {
  return 'getScreenDetails' in window;
};
